"use client";

import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { en } from "./en";
import { ml } from "./ml";
import type { Language, Translations } from "./types";

const translations: Record<Language, Translations> = {
  en,
  ml,
};

type I18nContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
};

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const LANGUAGE_STORAGE_KEY = "etheraguru-language";

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>("en");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Only access localStorage on client
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null;
    if (stored && (stored === "en" || stored === "ml")) {
      setLanguageState(stored);
    }
    setMounted(true);
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== "undefined") {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    }
  };

  // Always provide context, but use default language until mounted to prevent hydration mismatch
  return (
    <I18nContext.Provider
      value={{
        language: mounted ? language : "en",
        setLanguage,
        t: translations[mounted ? language : "en"],
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useTranslation must be used within I18nProvider");
  }
  return context;
}
